import { type InputHTMLAttributes, type JSX, type ReactNode, useId } from 'react';
import { cx } from './primitives';

/**
 * `FormField` - the label / control / hint / error wrapper every form on every
 * portal uses.
 *
 * It exists to make the accessible wiring impossible to get wrong rather than to
 * save markup: the label is bound to the control by id, the hint and the error
 * are bound through `aria-describedby`, the error is announced through a live
 * region, and an invalid control is marked `aria-invalid` (PRD NFR-005). A form
 * that assembles those by hand gets one of them wrong eventually, and the guest
 * checkout is the one form on the platform that has to work first time.
 *
 * The control is supplied by the caller as a render prop so this component never
 * has to know whether it wraps an input, a select, a textarea or a group of
 * radios - and so nothing here needs a form library (tech-stack section 11).
 *
 * Every string is passed in already translated; no copy is hardcoded in a
 * component (PRD NFR-006).
 */

export interface FieldControlProps {
  /** Put this on the control so the label points at it. */
  id: string;
  /** Space-separated ids of the hint and error, or undefined when neither shows. */
  'aria-describedby': string | undefined;
  /** True while `error` is set. */
  'aria-invalid': boolean | undefined;
  /** Mirrors `required`, for controls that are not native inputs. */
  'aria-required': boolean | undefined;
  required: boolean | undefined;
}

export interface FormFieldProps {
  /** Already translated. */
  label: ReactNode;
  /** Renders the control, wired to the ids this component generated. */
  children: (control: FieldControlProps) => ReactNode;
  /** Supporting text shown under the label. Already translated. */
  hint?: ReactNode;
  /**
   * Validation message. Its presence is what marks the field invalid, so a
   * caller cannot show an error without also marking the control - and cannot
   * mark the control without showing an error. Already translated.
   */
  error?: ReactNode;
  required?: boolean;
  /**
   * Visible marker for a required field. Passed in because "(required)" is copy
   * and copy is never hardcoded here (PRD NFR-006). An asterisk alone is not
   * sufficient: it is not announced and not understood universally.
   */
  requiredLabel?: ReactNode;
  /**
   * The mirror of `requiredLabel`, for a field that may be left empty on a form
   * where most may not. It sits beside the label in the same muted marker, so
   * "(optional)" is read where the eye already is rather than as a sentence of
   * helper text under it - which is what it used to be, and which said the same
   * thing in more words on the one screen a guest reads one-handed.
   *
   * Passed in because it is copy, for the same reason as `requiredLabel`.
   */
  optionalLabel?: ReactNode;
  /** Renders the label to assistive technology only. The control still gets it. */
  labelHidden?: boolean;
  /**
   * Edited-but-unsaved: the control's value differs from what the platform has
   * stored. Marks the control with an amber border and the label with a dot,
   * until the edit is saved or reverted. Computed by the caller against the
   * loaded record - the same baseline its form's `SaveButton` uses.
   */
  dirty?: boolean;
  /**
   * `row` puts the label (and hint) on the left and the control on the right,
   * on one compact line - the settings-screen layout on every portal. It
   * collapses back to the stacked form on narrow viewports, so a phone never
   * pays for it. `stack` (the default) keeps the label above the control, for
   * narrow containers - filter bars, sign-in cards - where a row cannot fit.
   */
  layout?: 'stack' | 'row';
  className?: string;
  /** Overrides the generated id when a caller needs a stable one (deep links, tests). */
  id?: string;
}

export function FormField({
  label,
  children,
  hint,
  error,
  required,
  requiredLabel,
  optionalLabel,
  labelHidden = false,
  layout = 'stack',
  dirty = false,
  className,
  id,
}: FormFieldProps): JSX.Element {
  const generatedId = useId();
  const controlId = id ?? `cib-field-${generatedId}`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;

  const describedBy = [hint !== undefined ? hintId : null, error !== undefined ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cx(
        'cib-field',
        layout === 'row' && 'cib-field--row',
        dirty && 'cib-field--dirty',
        error !== undefined && 'cib-field--invalid',
        className,
      )}
    >
      <label
        className={cx('cib-field__label', labelHidden && 'cib-visually-hidden')}
        htmlFor={controlId}
      >
        {label}
        {required && requiredLabel !== undefined ? (
          <span className="cib-field__required">{requiredLabel}</span>
        ) : null}
        {!required && optionalLabel !== undefined ? (
          <span className="cib-field__required">{optionalLabel}</span>
        ) : null}
      </label>

      {hint !== undefined ? (
        <p className="cib-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}

      {children({
        id: controlId,
        'aria-describedby': describedBy === '' ? undefined : describedBy,
        'aria-invalid': error !== undefined ? true : undefined,
        'aria-required': required === true ? true : undefined,
        required,
      })}

      {/*
       * Always rendered so the live region exists before the message does -
       * a region inserted at the same moment as its content is not reliably
       * announced.
       */}
      <p className="cib-field__error" id={errorId} role="alert">
        {error}
      </p>
    </div>
  );
}

/**
 * The plain text input, pre-styled. A caller that needs anything else - select,
 * textarea, a Stripe element - renders it directly inside `FormField` with the
 * same control props; this is a convenience, never a requirement.
 */
export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  className?: string;
}

export function TextInput({ className, type = 'text', ...rest }: TextInputProps): JSX.Element {
  return <input {...rest} type={type} className={cx('cib-input', className)} />;
}
